/**
 * Returns the difference in days between two dates.
 *
 * @example
 * ```ts
 * const dateLeft = new Date('2021-01-01');
 * const dateRight = new Date('2021-01-02');
 * const difference = getDateDifferenceInDays(dateLeft, dateRight);
 * console.log(difference); // -1
 * ```
 */
export const getDateDifferenceInDays = (
  dateLeft: Date,
  dateRight: Date,
): number => {
  const dayInMs = 86_400_000;
  const timeDiff = dateLeft.getTime() - dateRight.getTime();
  return Math.ceil(timeDiff / dayInMs);
};

/**
 * Returns the difference in months between two dates.
 *
 * @example
 * ```ts
 * const dateLeft = new Date('2021-01-01');
 * const dateRight = new Date('2021-02-01');
 * const difference = getDateDifferenceInMonths(dateLeft, dateRight);
 * console.log(difference); // -1
 * ```
 */
export const getDateDifferenceInMonths = (
  dateLeft: Date,
  dateRight: Date,
): number => {
  const yearDiff = dateLeft.getFullYear() - dateRight.getFullYear();
  const monthDiff = dateLeft.getMonth() - dateRight.getMonth();
  return yearDiff * 12 + monthDiff;
};

/**
 * Returns true if current date is a month or less from now and false otherwise
 *
 * @example
 * ```ts
 * const date = new Date('2030-01-01');
 * const isMonthFromNow = isOneMonthFromNow(date);
 * console.log(isMonthFromNow); // false
 * ```
 */
export function isOneMonthFromNow(date: Date | null) {
  if (date === null) return false;
  let currentDate = new Date();

  const timeDifference = date.getTime() - currentDate.getTime();
  const diffInDays = timeDifference / (1000 * 60 * 60 * 24);

  return diffInDays <= 31 && diffInDays > 0;
}
